import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Layers,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import baseapi from '../../../../services/baseapi';
import { ItemsPerPageDropdown } from '../../../../components/ItemsPerPageDropdown ';

export interface ClickhousePartsProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

export interface ClickhousePartItem {
  active_parts: number;
  agent_id?: number;
  asset_id?: number;
  bytes_on_disk: number;
  client_id?: number;
  cluster_id?: string;
  collected_at?: string;
  compressed_bytes?: number;
  config_id?: string;
  database?: string;
  db_type?: string;
  hostname?: string;
  indexed_at?: string;
  ip?: string;
  kind?: string;
  log_timestamp?: string;
  master_agent_id?: string;
  organization_id?: number;
  os?: string;
  rows: number;
  table: string;
  uncompressed_bytes: number;
}

export interface ClickhousePartsApiResponse {
  status: boolean;
  applicable: boolean;
  parts_fresh?: boolean;
  detached_parts_fresh?: boolean;
  parts: ClickhousePartItem[];
  detached_parts?: any[];
  detached_parts_by_reason?: Record<string, any>;
  total_active_parts?: number;
  detached_count?: number | null;
  total_count?: number;
  total?: number;
  count?: number;
  [key: string]: any;
}

const formatBytes = (bytes: number | undefined | null): string => {
  if (bytes === undefined || bytes === null || isNaN(bytes)) return '0 B';
  const b = Number(bytes);
  if (b === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(Math.abs(b)) / Math.log(k));
  const safeI = Math.min(Math.max(i, 0), sizes.length - 1);
  const val = b / Math.pow(k, safeI);
  return `${Number(val.toFixed(1))} ${sizes[safeI]}`;
};

export const ClickhouseParts: React.FC<ClickhousePartsProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
}) => {
  const [parts, setParts] = useState<ClickhousePartItem[]>([]);
  const [partsFresh, setPartsFresh] = useState<boolean>(true);
  const [totalActiveParts, setTotalActiveParts] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // API-level Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const fetchParts = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);
    setError(null);

    const minutes = parseInt(timeRange || '60', 10);
    const skip = (currentPage - 1) * pageSize;
    const limit = pageSize;

    const params: any = {
      cluster_id: activeCluster,
      minutes,
      skip,
      limit,
    };
    if (clientId) {
      params.client_id = clientId;
    }

    try {
      const res = await baseapi.get('/db_monitor_dashboard/clickhouse/parts', { params });
      const resData: ClickhousePartsApiResponse = res.data?.data || res.data;
      const partList = Array.isArray(resData?.parts) ? resData.parts : [];
      setParts(partList);
      setPartsFresh(resData?.parts_fresh ?? true);
      if (resData?.total_active_parts != null) {
        setTotalActiveParts(Number(resData.total_active_parts));
      }

      // Read total items directly from API total_count / total key
      const apiTotal = resData?.total_count ?? resData?.total ?? res.data?.total_count ?? res.data?.total;
      if (apiTotal !== undefined && apiTotal !== null && !isNaN(Number(apiTotal))) {
        setTotalCount(Number(apiTotal));
      } else {
        setTotalCount(partList.length);
      }
    } catch (err: any) {
      console.warn('ClickhouseParts fetch error:', err);
      setError(
        err?.response?.data?.message || err?.message || 'Failed to load ClickHouse parts'
      );
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId, currentPage, pageSize]);

  useEffect(() => {
    fetchParts();
  }, [fetchParts, refreshKey]);

  // Total pages and navigation calculations
  const totalPages = useMemo(() => {
    if (totalCount != null) {
      return Math.max(1, Math.ceil(totalCount / pageSize));
    }
    return parts.length === pageSize ? currentPage + 1 : currentPage;
  }, [totalCount, pageSize, parts.length, currentPage]);

  // Pagination states and helpers matching User.tsx
  const totalItems = totalCount ?? parts.length;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  const handlePageChange = (page: number): void => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (value: number): void => {
    setPageSize(value);
    setCurrentPage(1);
  };

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 sm:p-5   space-y-4">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
            <Layers className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                Table Parts
              </h3>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold font-mono bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                {totalCount != null ? `${totalCount} Tables` : `${parts.length} on page`}
              </span>
              {totalActiveParts != null && (
                <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30">
                  {totalActiveParts} Active Parts
                </span>
              )}
              <span className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                <span
                  className={`w-2 h-2 rounded-full ${
                    partsFresh ? 'bg-emerald-500' : 'bg-gray-400'
                  }`}
                />
                {partsFresh ? 'Fresh' : 'Stale'}
              </span>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              Active parts count, row distribution, disk consumption, and uncompressed size
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2.5">
          {/* Refresh Button */}
          <button
            onClick={() => fetchParts()}
            disabled={loading}
            className="p-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 transition-colors   cursor-pointer disabled:opacity-50 h-8 w-8 flex items-center justify-center"
            title="Refresh Parts"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Error state */}
      {!loading && error && (
        <div className="p-4 bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-800/40 rounded-xl text-center">
          <AlertTriangle className="w-5 h-5 text-rose-500 mx-auto mb-1.5" />
          <p className="text-xs text-rose-600 dark:text-rose-400">{error}</p>
          <button
            onClick={() => fetchParts()}
            className="mt-2 px-3 py-1 bg-rose-600 text-white rounded-lg text-xs font-semibold hover:bg-rose-700 transition-colors cursor-pointer"
          >
            Retry
          </button>
        </div>
      )}

      {/* Table & Footer Container */}
      <div>
        {/* Table Data View */}
        <div className="overflow-x-auto overflow-y-auto custom-scroll border border-gray-200 dark:border-gray-700/60 rounded-t-xl min-h-[160px]">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900   text-[11px] text-gray-600 dark:text-gray-400 uppercase tracking-wider select-none">
                <th className="py-2.5 px-3.5 bg-gray-50 dark:bg-gray-900">Table</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Parts</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Rows</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">On disk</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Uncompressed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60 text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900/40">
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-500 mx-auto mb-2" />
                    Loading ClickHouse parts...
                  </td>
                </tr>
              ) : parts.length > 0 ? (
                parts.map((item, idx) => {
                  return (
                    <tr
                      key={`${item.database || 'default'}-${item.table}-${idx}`}
                      className="hover:bg-gray-50/80 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      {/* Table Name */}
                      <td className="py-2.5 px-3.5 font-mono">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900 dark:text-gray-100">
                            {item.table}
                          </span>
                          {item.database && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700/60 font-sans">
                              {item.database}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Parts */}
                      <td className="py-2.5 px-3.5 text-right font-mono">
                        <span className="inline-block px-2 py-0.5 rounded text-[11px] font-bold bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30">
                          {item.active_parts != null ? item.active_parts.toLocaleString() : '—'}
                        </span>
                      </td>

                      {/* Rows */}
                      <td className="py-2.5 px-3.5 text-right font-mono font-medium text-gray-700 dark:text-gray-300">
                        {item.rows != null ? item.rows.toLocaleString() : '—'}
                      </td>

                      {/* On disk */}
                      <td className="py-2.5 px-3.5 text-right font-mono font-bold text-orange-600 dark:text-orange-400">
                        {formatBytes(item.bytes_on_disk)}
                      </td>

                      {/* Uncompressed */}
                      <td className="py-2.5 px-3.5 text-right font-mono text-gray-600 dark:text-gray-400">
                        {formatBytes(item.uncompressed_bytes)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-400 dark:text-gray-500 italic">
                    No ClickHouse parts found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer */}
        <div className="bg-gray-50/80 dark:bg-gray-800/50 border border-t-0 rounded-b-xl border-gray-200 dark:border-gray-700 px-3 py-1.5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <ItemsPerPageDropdown
              value={pageSize}
              onChange={handleItemsPerPageChange}
            />
            <span className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest">
              <span className="text-gray-900 dark:text-gray-200">
                {parts.length === 0 ? 0 : startIndex + 1}
              </span>{' '}
              -{' '}
              <span className="text-gray-900 dark:text-gray-200">
                {Math.min(endIndex, totalItems)}
              </span>{' '}
              OF {totalItems}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1 || loading}
              className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"
              title="First Page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || loading}
              className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"
              title="Previous Page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5   dark:shadow-lg dark:shadow-orange-900/20">
              {currentPage}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages || loading}
              className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"
              title="Next Page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage >= totalPages || loading}
              className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"
              title="Last Page"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClickhouseParts;
